import React, { useState } from 'react';
import { MdOutlineRemoveRedEye, MdVisibilityOff } from 'react-icons/md';
import { useAuth } from 'contexts/AuthContext';
import { API } from 'utilities/api';
import { toast } from 'react-toastify';
import { T, inputFocus, inputBlur } from './authStyles';
import OAuthButtons from './OAuthButtons';
import Divider from './Divider';

/**
 * Email/password sign-in with a follow-up 2FA code step.
 *
 * Flow:
 *   1. signIn(email, password)              → backend emails a code
 *   2. verificationMail(email, code)        → backend returns Token + user
 *   3. login(token, user) caches both       → onSuccess(user) lets the
 *                                             modal decide the redirect
 *
 * The verify response now includes `user.is_platform_admin`, so the
 * parent doesn't need a second roundtrip to route admins to /admin.
 */
export default function SignIn({ onSuccess }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifyStep, setVerifyStep] = useState(false);
  const [code, setCode] = useState('');
  const { login } = useAuth();

  const handleSignIn = async () => {
    if (!email || !password) { toast.error('Please fill all fields'); return; }
    setLoading(true);
    try {
      await API.signIn({ email_address: email, password });
      setVerifyStep(true);
      toast.info('Verification code sent to your email.');
    } catch (err) { toast.error(err?.message || 'Invalid email or password'); }
    finally { setLoading(false); }
  };

  const handleVerify = async () => {
    if (!code) { toast.error('Enter the verification code'); return; }
    setLoading(true);
    try {
      const res = await API.verificationMail({ email_address: email, confirmationCode: code });
      const token = res?.Token || res?.token;
      if (!token) { toast.error('Verification failed. Please try again.'); return; }
      login(token, res.user || null);
      toast.success('Welcome back!');
      onSuccess?.(res.user || null);
    } catch (err) { toast.error(err?.message || 'Invalid verification code'); }
    finally { setLoading(false); }
  };

  if (verifyStep) return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, textAlign: 'center', margin: 0 }}>
        Verification code sent to <b>{email}</b>
      </p>
      <input style={T.input} placeholder="Verification code" value={code} onChange={e => setCode(e.target.value)} onFocus={inputFocus} onBlur={inputBlur} />
      <button style={{ ...T.primaryBtn, opacity: loading ? 0.7 : 1 }} onClick={handleVerify} disabled={loading}>{loading ? 'Verifying…' : 'Verify'}</button>
      <button style={{ background: 'none', border: 'none', color: '#A78BFA', fontSize: 13, cursor: 'pointer' }} onClick={() => API.resendVerificationMail({ email_address: email })}>Resend code</button>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <OAuthButtons onSuccess={onSuccess} />
      <Divider label="or continue with email" />
      <div>
        <label style={T.label}>Email</label>
        <input style={T.input} type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} onFocus={inputFocus} onBlur={inputBlur} />
      </div>
      <div>
        <label style={T.label}>Password</label>
        <div style={{ position: 'relative' }}>
          <input
            style={{ ...T.input, paddingRight: 44 }}
            type={showPass ? 'text' : 'password'}
            placeholder="Min. 8 characters"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSignIn()}
            onFocus={inputFocus}
            onBlur={inputBlur}
          />
          <button onClick={() => setShowPass(v => !v)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.45)', display: 'flex', alignItems: 'center' }}>
            {showPass ? <MdVisibilityOff size={18} /> : <MdOutlineRemoveRedEye size={18} />}
          </button>
        </div>
      </div>
      <button
        style={{ ...T.primaryBtn, opacity: loading ? 0.7 : 1 }}
        onClick={handleSignIn}
        disabled={loading}
        onMouseEnter={e => { if (!loading) { e.currentTarget.style.opacity = '0.88'; e.currentTarget.style.transform = 'translateY(-1px)'; }}}
        onMouseLeave={e => { e.currentTarget.style.opacity = loading ? '0.7' : '1'; e.currentTarget.style.transform = 'none'; }}
      >
        {loading ? 'Signing in…' : 'Sign In'}
      </button>
    </div>
  );
}
